import { AxiosResponse } from "axios";
import { AxiosHelper } from "../axiosConfig";
import { PagingResponse } from "../model/interface/pagingResponse";
import APIConstant from "../utils/apiConstant";

export interface IUserInfo {
  userId: number;
  firstName: string;
  lastName: string;
  district: string;
  roleId: number;
  districtId: number;
}

export interface ITeacherPassage {
  teacherPassageId: number;
  createdDate: string;
  title: string;
  gradeId: number;
  gradeName: string;
  isActive: boolean;
  isApproved: boolean;
  wordCount: number;
  cmsAuthorUserId: number;
  ownerUserId?: number | null;
  ownerFirstName?: string;
  ownerLastName?: string;
  authorUserId?: number | null;
  authorFirstName?: string;
  authorLastName?: string;
}

export interface ITeacherPassageSearchRequest {
  teacherPassageId?: number;
  title?: string;
  gradeId?: number;
  isActive?: boolean;
  isApproved?: boolean;
  pageSize: number;
  pageNumber: number;
  orderByColumn: string;
  sortByDesc: boolean;
  districtId?: number;
}

export const getUserInfo = (
  userId: number
): Promise<AxiosResponse<IUserInfo>> => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_CRM_CONTENT_USER_INFO.replace("{userId}", userId.toString())
  );
};

export const getTeacherPassages = (
  request: ITeacherPassageSearchRequest
): Promise<AxiosResponse<PagingResponse<ITeacherPassage>>> => {
  return AxiosHelper.axiosInstance().get<PagingResponse<ITeacherPassage>>(
    APIConstant.CMS_TEACHER_PASSAGE,
    {
      params: request,
    }
  );
};

export interface ITeacherQuestion {
  teacherQuestionId: number;
  createdDate: string;
  questionTypeId: number;
  questionTypeName: string;
  subjectName?: string;
  domainName?: string;
  standardName?: string;
  standardCode?: string;
  ownerUserId: number;
  ownerFirstName: string;
  ownerLastName: string;
  authorUserId?: number;
  authorFirstName?: string;
  authorLastName?: string;
}

export interface ITeacherQuestionSearchRequest {
  pageSize: number;
  pageNumber: number;
  orderByColumn: string;
  sortByDesc: boolean;
  districtId?: number;
  subjectId?: number;
  domainId?: number;
  standardId?: number;
  dok?: number;
  isPublished?: boolean;
  questionTypeId?: number;
  teacherQuestionId?: number;
}

export interface ICmsSubject {
  subjectId: number;
  name: string;
}

export interface ICmsQuestionType {
  questionTypeId: number;
  name: string;
  displayName?: string;
  externalCode?: string;
}

export const getTeacherQuestions = (
  request: ITeacherQuestionSearchRequest
): Promise<AxiosResponse<PagingResponse<ITeacherQuestion>>> => {
  return AxiosHelper.axiosInstance().get<PagingResponse<ITeacherQuestion>>(
    APIConstant.CMS_TEACHER_QUESTION,
    {
      params: request,
    }
  );
};

export const getCmsTeacherSubjects = (
  teacherId: number
): Promise<AxiosResponse<Array<ICmsSubject>>> => {
  return AxiosHelper.axiosInstance().get<Array<ICmsSubject>>(
    APIConstant.CMS_TEACHER_SUBJECTS,
    {
      params: { teacherId },
    }
  );
};

export const getCmsQuestionTypes = (): Promise<
  AxiosResponse<Array<ICmsQuestionType>>
> => {
  return AxiosHelper.axiosInstance().get<Array<ICmsQuestionType>>(
    APIConstant.CMS_TEACHER_QUESTION_TYPE
  );
};

export interface ITeacherPassageDetail {
  teacherPassageId: number;
  title: string;
  passage: string;
  passageReferenceId: string;
  gradeId: number | null;
  wordCount: number | null;
  isActive: boolean;
  isApproved: boolean;
  cmsAuthorNotes: string;
}

export interface ICreateTeacherPassageRequest {
  userId: number;
  isApproved: boolean;
  isActive: boolean;
  gradeId: number;
  cmsAuthorUserId?: number;
  title?: string;
}

export interface IUpdateTeacherPassageRequest {
  teacherPassageId: number;
  userId: number;
  cmsAuthorUserId?: number;
  title: string;
  passage: string;
  passageReferenceId: string;
  gradeId: number;
  wordCount: number;
  isApproved: boolean;
  isActive: boolean;
  cmsAuthorNotes: string;
  passageWidgetJson?: string;
}

export const getTeacherPassageById = (
  id: number,
  districtId: number
): Promise<AxiosResponse<ITeacherPassageDetail>> =>
  AxiosHelper.axiosInstance().get(
    `${APIConstant.CMS_TEACHER_PASSAGE}/${id}?districtId=${districtId}`
  );

export interface ITeacherPassageQuestion {
  teacherQuestionId: number;
  questionText: string | null;
  questionReferenceId: string | null;
}

export const getTeacherPassageQuestions = (
  teacherPassageId: number,
  districtId: number
): Promise<AxiosResponse<Array<ITeacherPassageQuestion>>> =>
  AxiosHelper.axiosInstance().get(
    `${APIConstant.CMS_TEACHER_PASSAGE}/${teacherPassageId}/questions?districtId=${districtId}`
  );

export const createTeacherPassage = (
  request: ICreateTeacherPassageRequest
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().post(APIConstant.CMS_TEACHER_PASSAGE, request);

export const updateTeacherPassage = (
  request: IUpdateTeacherPassageRequest
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().put(APIConstant.CMS_TEACHER_PASSAGE, request);

export const deleteTeacherPassage = (
  teacherPassageId: number
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().delete(
    `${APIConstant.CMS_TEACHER_PASSAGE}/${teacherPassageId}`
  );

export interface IAssessment {
  assessmentId: number;
  name: string;
  code: string;
  subject: string;
  questionsCount: number;
  ownerUserId: number;
  ownerFirstName: string;
  ownerLastName: string;
  authorUserId: number | null;
  authorFirstName: string | null;
  authorLastName: string | null;
  createdDate: string;
  learnosityIdEnglish?: string;
  learnosityIdSpanish?: string;
  assessmentStatusId: number;
}

export interface IAssessmentSearchRequest {
  teacherId: number;
  name?: string;
  code?: string;
  subjectId?: number;
  pageNumber: number;
  pageSize: number;
  orderByColumn: string;
  sortByDesc: boolean;
}

export const getCmsAssessments = (
  request: IAssessmentSearchRequest
): Promise<AxiosResponse<PagingResponse<IAssessment>>> => {
  return AxiosHelper.axiosInstance().get<PagingResponse<IAssessment>>(
    APIConstant.CMS_ASSESSMENT,
    { params: request }
  );
};

export const createCmsAssessment = (
  formData: FormData
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().post(APIConstant.CMS_ASSESSMENTS, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export interface IAssessmentDetail {
  assessmentId: number;
  name: string;
  subjectId: number;
  subjectName: string;
  assessmentStatusId: number;
  isSharedAssessment: boolean;
  // Read as sharedTypeId here, sent back as assessmentSharedTypeId on update.
  sharedTypeId: number | null;
  questions: IAssessmentQuestionDetail[];
}

export interface IAssessmentQuestionDetail {
  questionId: number;
  assessmentQuestionTypeId: number;
  standardId: number;
  standardName: string;
  standardCode?: string;
  sequenceNumber: number;
  learnosityReferenceId: string | null;
}

export interface IUpdateAssessmentRequest {
  name: string;
  subjectId: number;
  // The API keeps the stored shared type when this is omitted or null.
  assessmentSharedTypeId?: number | null;
  questions: IUpdateAssessmentQuestion[];
}

export interface IUpdateAssessmentQuestion {
  questionId: number;
  standardId: number | null;
  isTeacherQuestion: boolean;
}

export const getCmsAssessmentById = (
  assessmentId: number
): Promise<AxiosResponse<IAssessmentDetail>> =>
  AxiosHelper.axiosInstance().get<IAssessmentDetail>(
    `${APIConstant.CMS_ASSESSMENTS}/${assessmentId}`
  );

export const updateCmsAssessment = (
  userId: number,
  assessmentId: number,
  request: IUpdateAssessmentRequest
): Promise<AxiosResponse<void>> =>
  AxiosHelper.axiosInstance().put(
    `${APIConstant.CMS_ASSESSMENTS}/${assessmentId}`,
    request,
    { params: { userId } }
  );

export const validateCmsAssessmentQuestion = (
  userId: number,
  questionId?: number,
  standardId?: number,
  teacherQuestionId?: number,
  subjectId?: number
): Promise<AxiosResponse<IAssessmentQuestionDetail>> =>
  AxiosHelper.axiosInstance().get<IAssessmentQuestionDetail>(
    `${APIConstant.CMS_ASSESSMENTS}/validate-question`,
    { params: { userId, questionId, standardId, teacherQuestionId, subjectId } }
  );

export interface ICreateTeacherQuestionRequest {
  userId: number;
  cmsAuthorUserId?: number;
  cmsAuthorNotes?: string;
  districtId: number;
  stateId: number;
  isPublished: boolean;
  questionTypeId: number;
  standardId: number;
  dok: number;
  passageIds: number[];
}

export const createTeacherQuestion = (
  request: ICreateTeacherQuestionRequest
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().post(APIConstant.CMS_TEACHER_QUESTION, request);

export interface ITeacherQuestionDetail {
  teacherQuestionId: number;
  questionText: string | null;
  questionReferenceId: string | null;
  questionTypeId: number;
  standardId: number;
  domainId: number;
  subjectId: number;
  stateId: number;
  isPublished: boolean;
  isActive: boolean;
  dok: number;
  isAuthorAide: boolean;
  createdByUserId: number;
  cmsAuthorUserId: number | null;
  cmsAuthorNotes: string | null;
  passageIds: number[];
}

export interface ISaveTeacherQuestionRequest {
  teacherQuestionId: number;
  questionText?: string;
  updatedByUserId: number;
  parts: Array<{ partIndex: number; questionRequest: string }>;
  passageIds: number[];
  stateId: number;
  standardId: number;
  isPublished: boolean;
  dok: number;
  cmsAuthorNotes?: string;
}

export const getTeacherQuestionById = (
  id: number
): Promise<AxiosResponse<ITeacherQuestionDetail>> =>
  AxiosHelper.axiosInstance().get(`${APIConstant.CMS_TEACHER_QUESTION}/${id}`);

export const saveTeacherQuestion = (
  request: ISaveTeacherQuestionRequest
): Promise<AxiosResponse<number>> =>
  AxiosHelper.axiosInstance().put(
    `${APIConstant.CMS_TEACHER_QUESTION}/save`,
    request
  );

export const getTeacherQuestionTypePayload = (
  code: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<AxiosResponse<any>> =>
  AxiosHelper.axiosInstance().get(
    APIConstant.CMS_TEACHER_QUESTION_TYPE_PAYLOAD,
    {
      params: { code },
    }
  );
